import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { FiActivity, FiRefreshCw } from 'react-icons/fi';
import Layout from '../components/Layout';
import AnalyticsDateRange from '../components/AnalyticsDateRange';
import { useAnalyticsChartRange, clipAnalyticsChartData, ANALYTICS_DATE_XAXIS_PROPS } from '../hooks/useAnalyticsChartRange';

const AdsAnalyticsPage = () => {
    const [dashboard, setDashboard] = useState(null);
    const [clickChart, setClickChart] = useState({ data: [], lines: [] });
    const { chartFrom, setChartFrom, chartTo, setChartTo, rangeReady } = useAnalyticsChartRange();
    const [chartLoading, setChartLoading] = useState(true);
    const [error, setError] = useState('');

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchDashboard = useCallback(async () => {
        try {
            setError('');
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/ads-analytics/dashboard`,
                { headers: authHeaders() }
            );
            if (res.data.success) setDashboard(res.data.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load analytics');
        }
    }, []);

    const fetchClickChart = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setChartLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/ads-analytics/chart`,
                {
                    headers: authHeaders(),
                    params: { from: chartFrom, to: chartTo },
                }
            );
            if (res.data.success) {
                setClickChart(res.data.data || { data: [], lines: [] });
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load chart');
        } finally {
            setChartLoading(false);
        }
    }, [chartFrom, chartTo]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    useEffect(() => {
        if (!rangeReady) return;
        fetchClickChart();
    }, [rangeReady, fetchClickChart]);

    const clickSeries = clipAnalyticsChartData(clickChart.data, chartFrom, chartTo);

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <FiActivity className="h-6 w-6 text-blue-500" />
                        <h1 className="text-2xl font-bold">Ads Analytics</h1>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <AnalyticsDateRange
                            chartFrom={chartFrom}
                            setChartFrom={setChartFrom}
                            chartTo={chartTo}
                            setChartTo={setChartTo}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                fetchDashboard();
                                fetchClickChart();
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            <FiRefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">
                        Click rate over time
                    </h2>

                    {chartLoading ? (
                        <p className="text-gray-500 py-12 text-center">Loading chart...</p>
                    ) : !clickSeries.length || !clickChart.lines?.length ? (
                        <p className="text-gray-500 py-12 text-center">No click data for selected range</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={360}>
                            <LineChart key={`ads-${chartFrom}-${chartTo}`} data={clickSeries}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis {...ANALYTICS_DATE_XAXIS_PROPS} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} label={{ value: 'Clicks', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: '11px' }} />
                                {(clickChart.lines || []).map((line) => (
                                    <Line
                                        key={line.key}
                                        type="monotone"
                                        dataKey={line.key}
                                        name={line.name}
                                        stroke={line.color}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default AdsAnalyticsPage;
